import { ChangeDetectorRef, Component, EventEmitter, Output, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { Product, ProductsComponent } from '../../components/products/products.component';
import { SubtitlesComponent } from '../../components/subtitles/subtitles.component';
import { CommonModule } from '@angular/common';
import { SpeechComponent } from '../../components/speech/speech.component';
import { RecommendationsFlowService, CurrentRecommendation } from '../services/recommendations-flow.service';
import { SelectedProductsService } from '../services/selected-products.service';
import { ApiService } from '../../service/api.service';
import { CategoryFilter } from '../../service/api.dto';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-step3-upsell',
  standalone: true,
  imports: [CommonModule, SpeechComponent, SubtitlesComponent, ProductsComponent],
  templateUrl: './step3-upsell.component.html',
  styleUrl: './step3-upsell.component.scss'
})
export class Step3UpsellComponent implements OnInit, OnDestroy {

  products: Product[] = [];
  currentRecommendation: CurrentRecommendation | null = null;
  private subscriptions = new Subscription();

  @ViewChild('speechComponent') speechComponent!: SpeechComponent;
  @ViewChild('subtitles') SubtitlesComponent!: SubtitlesComponent;
  @ViewChild('productComponent', { static: false }) productComponent!: ProductsComponent;
  
  @Output() productSelected = new EventEmitter<Product | null>();
  @Output() stageCompleted = new EventEmitter<void>();

  private stageCompletedEmitted = false;

  constructor(
    private recoFlow: RecommendationsFlowService,
    private cdr: ChangeDetectorRef,
    private apiService: ApiService,
    private selectionService: SelectedProductsService
  ) {}

  ngOnInit() {
    // Suscribirse a las recomendaciones del servicio (solo upsell)
    this.subscriptions.add(
      this.recoFlow.currentRecommendation$.subscribe(recommendation => {
        if (recommendation && recommendation.type === 'upsell') {
          this.currentRecommendation = recommendation;
          this.products = [recommendation.product];
          this.SubtitlesComponent.render(recommendation.message);
          this.cdr.detectChanges();
          
          if (this.productComponent) {
            this.productComponent.loadProducts();
            setTimeout(() => {
              if (this.productComponent) {
                this.productComponent.refreshSplide();
              }
            }, 200);
          }
        } else if (recommendation && recommendation.type === 'cross') {
          this.handleStageCompletion();
        } else if (!recommendation && !this.apiService.integratedRecommendationsFlow) {
          this.currentRecommendation = null;
          this.products = [];
        }
      })
    );

    this.subscriptions.add(
      this.recoFlow.flowFinished$.subscribe(finished => {
        if (finished) {
          // Cuando termine el flujo de upsell, hacer la llamada batch de cross-sell
          this.loadCrossSellingBatch();
        }
      })
    );

    this.subscriptions.add(
      this.recoFlow.errorMessage$.subscribe(message => {
        if (!message) {
          return;
        }

        this.currentRecommendation = null;
        this.products = [];
        this.SubtitlesComponent.render(message);
        this.cdr.detectChanges();
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  onProductSelected(product: Product) {
    if (this.apiService.integratedRecommendationsFlow) {
      const result = this.recoFlow.acceptCurrent(product);
      if (result) {
        this.selectionService.upsertSelection(result.todoId, product, 'upsell');
      }
      return;
    }
    this.productSelected.emit(product);
  }

  noInterest() {
    if (this.apiService.integratedRecommendationsFlow) {
      this.recoFlow.rejectCurrent();
      return;
    }
    this.recoFlow.rejectCurrent();
    this.productSelected.emit(null);
  }

  /**
   * Determina el mensaje de upselling según upselling_text_type
   * Si es "custom", usa upselling_text del POS
   * Si es "ai_generated", usa el mensaje del backend
   */
  private getUpsellingMessage(backendMessage?: string, fallback?: string): string {
    const posDetails = this.apiService.details;
    if (!posDetails) {
      return backendMessage || fallback || '';
    }

    // Si upselling_text_type es "custom", usar upselling_text
    if (posDetails.upselling_text_type === 'custom') {
      return posDetails.upselling_text || fallback || '';
    }

    // Si es "ai_generated" o cualquier otro valor, usar el mensaje del backend
    return backendMessage || fallback || '';
  }

  // Método para compatibilidad con AppComponent (flujo legacy)
  setProducts(recommendations: any) {
    if (recommendations.upsellingProducts?.producto) {
      const product = recommendations.upsellingProducts.producto;
      this.products = [{
        load: false,
        productID: product.id,
        name: product.name,
        price: product.price,
        image: product.photourl,
        description: product.search_aggregate
      }];
      const message = this.getUpsellingMessage(
        recommendations.upsellingProducts.explicacionDeRecomendacion,
        '¿Querés mejorar tu producto?'
      );
      this.SubtitlesComponent.render(message);
      this.cdr.detectChanges();
      
      if (this.productComponent) {
        this.productComponent.loadProducts();
        setTimeout(() => {
          if (this.productComponent) {
            this.productComponent.refreshSplide();
          }
        }, 200);
      }
    }
  }

  private loadCrossSellingBatch() {
    if (!this.apiService.integratedRecommendationsFlow) {
      this.handleStageCompletion();
      return;
    }

    const posDetails = this.apiService.details;
    if (!posDetails || !posDetails.cross_selling_enabled) {
      this.handleStageCompletion();
      return;
    }

    // El cross-sell ya debería estar cargándose en segundo plano desde que se inició el flujo
    // Esperamos a que esté listo antes de completar la etapa
    if (this.recoFlow.isCrossSellingBatchLoaded()) {
      // Ya está cargado, completar inmediatamente
      this.handleStageCompletion();
      return;
    }

    // Si aún no está cargado, esperar hasta que esté listo
    // No hay timeout porque queremos mostrar el cross-sell aunque tarde
    const checkInterval = setInterval(() => {
      if (this.recoFlow.isCrossSellingBatchLoaded()) {
        clearInterval(checkInterval);
        this.handleStageCompletion();
      }
    }, 100);

    // Timeout de seguridad más largo (10 segundos) para evitar esperar indefinidamente
    // pero solo si realmente no hay respuesta
    setTimeout(() => {
      clearInterval(checkInterval);
      // Si después de 10 segundos aún no está cargado, verificar si hay productos
      // Si no hay productos, completar. Si hay productos pero aún cargando, esperar más
      if (this.recoFlow.isCrossSellingBatchLoaded()) {
        this.handleStageCompletion();
      } else {
        // Esperar un poco más (otros 5 segundos) antes de omitir
        setTimeout(() => {
          if (this.recoFlow.isCrossSellingBatchLoaded()) {
            this.handleStageCompletion();
          } else {
            // Solo después de 15 segundos totales, omitir el cross-sell
            console.warn('Cross-sell tardó demasiado, omitiendo...');
            this.handleStageCompletion();
          }
        }, 5000);
      }
    }, 10000);
  }

  private handleStageCompletion() {
    if (!this.apiService.integratedRecommendationsFlow || this.stageCompletedEmitted) {
      return;
    }
    this.stageCompletedEmitted = true;
    this.currentRecommendation = null;
    this.products = [];
    this.stageCompleted.emit();
    this.cdr.detectChanges();
  }
}
